/**
 * usePatientList Hook
 *
 * Business logic for the patient list (ADR-012):
 * - One canonical filter state: status, payers, coverage, missing requirements
 * - Name search that composes with the filters
 * - Server-side filtering, counting, and pagination
 * - Event tracking
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import type { FiltersState } from '@components/ui/FilterMenu/types';
import { useAccessToken } from '@features/user';
import { useEventTracking } from '@services/statsig';
import debounce from 'lodash/debounce';

import APIService from '../../../services/APIService';
import SentryService from '../../../services/SentryService';
import type { Patient } from '../../../types/types';
import type { PatientsResponse } from '../types';
import {
  activityStatus,
  appliedFiltersMatch,
  buildListEndpoint,
  LIST_FIELDS,
  type ListCategory,
  paramsToView,
  requestableFilters,
  requirementKey,
  selectedValues,
  toggleValue,
  type UrlView,
  viewToParams,
} from '../utils/patientListQuery';
import type { PatientRequirementBlockDto, RequirementColumnView } from '../utils/requirementColumn';
import { columnsForActiveFilter, toRequirementColumnForBlocks } from '../utils/requirementColumn';

/** Where a filter change came from, for analytics. */
export type FilterSource = 'filter_bar' | 'sidebar';

const CATEGORIES: ListCategory[] = ['payers', 'coverage', 'missing'];

export interface UsePatientListOptions {
  initialPageSize?: number;
  /**
   * Whether this user may filter by requirements (gate and `manage_billing`).
   * Requirement ids in a shared link are dropped otherwise: the server
   * answers them with 403.
   */
  canFilterRequirements?: boolean;
}

export interface UsePatientListReturn {
  patients: Patient[];
  isLoading: boolean;
  error: string | null;
  /**
   * The server could not confirm it applied this combination (ADR-012
   * invariant 5). No rows are shown rather than rows for a different question.
   */
  filtersUnavailable: boolean;
  searchTerm: string;
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isEmpty: boolean;
  isSearchActive: boolean;
  filters: FiltersState;
  hasFilters: boolean;
  activityFilter: boolean | null;
  setActivityFilter: (active: boolean | null) => void;
  setFilters: (next: FiltersState, source?: FilterSource) => void;
  toggleFilterValue: (category: ListCategory, value: string, source?: FilterSource) => void;
  /** Clears the filters and keeps the search. */
  clearFilters: () => void;
  /** Clears the search and the filters. */
  resetView: () => void;
  setSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  clearSearch: () => void;
  /**
   * Each patient's standing on the selected requirements, keyed by patient id.
   * Empty unless requirements are selected, and withheld the moment the
   * selection changes.
   */
  requirementColumns: Record<string, RequirementColumnView>;
  refresh: () => void;
  navigateToPatient: (patientId: string) => void;
  handlePatientCreated: (patientId: string) => void;
}

type ListRow = Patient & {
  patient_requirement?: PatientRequirementBlockDto;
  patient_requirements?: Record<string, PatientRequirementBlockDto>;
};

const hasAnyFilter = (filters: FiltersState): boolean => Object.keys(filters).length > 0;

export function usePatientList(options: UsePatientListOptions = {}): UsePatientListReturn {
  const { initialPageSize = 20, canFilterRequirements = false } = options;

  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const { patient: patientMetrics, search: searchMetrics } = useEventTracking();

  // State
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersUnavailable, setFiltersUnavailable] = useState(false);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /**
   * Filters and page live in the URL; the name search lives in history state
   * (see `UrlView` for why). Back, Forward, and reload restore all three. The
   * search box also keeps its own state so typing never waits on navigation.
   */
  const location = useLocation();
  const navigationType = useNavigationType();
  const historySearch = (location.state as { q?: string } | null)?.q ?? '';
  const urlView = useMemo(
    () => paramsToView(new URLSearchParams(location.search)),
    [location.search],
  );
  /**
   * Back and Forward re-seed the search from the entry being restored. Done
   * during render, not in an effect: the fetch effect must see the restored
   * search together with the restored filters, never the new filters with the
   * old search.
   */
  const [searchState, setSearchState] = useState({ key: location.key, q: historySearch });
  if (searchState.key !== location.key) {
    setSearchState({
      key: location.key,
      q: navigationType === NavigationType.Pop ? historySearch : searchState.q,
    });
  }
  const searchTerm =
    searchState.key !== location.key && navigationType === NavigationType.Pop
      ? historySearch
      : searchState.q;
  const setSearchTermState = useCallback(
    (q: string) => setSearchState(current => ({ ...current, q })),
    [],
  );
  const { page } = urlView;
  /** Requirement ids stay in the URL but reach a request only when allowed. */
  const filters = useMemo(
    () => requestableFilters(urlView.filters, canFilterRequirements),
    [urlView.filters, canFilterRequirements],
  );
  /**
   * History policy: a filter change or a page change is one Back step. Typing
   * is one Back step per burst: the first keystroke after a pause pushes, and
   * the keystrokes that follow within a second replace it.
   */
  const lastSearchWriteRef = useRef(0);
  /**
   * The view as last written, ahead of the render that reads it back. Two
   * changes in one tick (a search and a status) must build on each other, not
   * each on the stale render: the second would overwrite the first.
   */
  type View = UrlView & { search: string };
  const pendingViewRef = useRef<View>({ ...urlView, search: searchTerm });
  useEffect(() => {
    pendingViewRef.current = { ...urlView, search: historySearch };
  }, [urlView, historySearch]);
  const writeView = useCallback(
    (update: (current: View) => View, mode: 'push' | 'replace' = 'push') => {
      // Any write but a keystroke ends a typing burst, so the next keystroke
      // pushes its own entry instead of replacing this one.
      if (mode === 'push') lastSearchWriteRef.current = 0;
      const next = update(pendingViewRef.current);
      pendingViewRef.current = next;
      const params = viewToParams(next).toString();
      navigate(
        { pathname: location.pathname, search: params ? `?${params}` : '' },
        { replace: mode === 'replace', state: next.search ? { q: next.search } : null },
      );
    },
    [navigate, location.pathname],
  );
  const setPage = useCallback(
    (nextPage: number) => {
      invalidateInFlightRef.current();
      writeView(current => ({ ...current, page: nextPage }));
    },
    [writeView],
  );
  /**
   * The columns, and the requirement selection they describe.
   *
   * Columns render only while they still describe the current selection:
   * between selections the previous response is still on screen, and a late
   * response for an old selection must not repaint them.
   */
  const [columnsByFilter, setColumnsByFilter] = useState<{
    definitionId: string | null;
    columns: Record<string, RequirementColumnView>;
  }>({ definitionId: null, columns: {} });

  // Refs for tracking
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialFetch = useRef(true);
  /**
   * Which fetch is the current one.
   *
   * The AbortController stops a response being RECEIVED; this counter stops a
   * stale one being APPLIED. A request that has already resolved cannot be
   * un-resolved by an abort. Rows and columns are accepted or rejected
   * together.
   */
  const requestSeqRef = useRef(0);
  const invalidateInFlightRef = useRef(() => {});

  /**
   * Retire whatever is in flight, now. The replacement fetch is issued by an
   * effect, so between the state change and the effect an obsolete response
   * could still resolve and match the current sequence. Invalidate first,
   * fetch later.
   */
  const invalidateInFlight = useCallback(() => {
    requestSeqRef.current += 1;
  }, []);
  invalidateInFlightRef.current = invalidateInFlight;

  const fetchPatients = useCallback(
    async (search: string, pageNum: number, pageSizeNum: number, query: FiltersState) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Held locally as well as on the ref: the signal is read after
      // `await getAccessToken()`, by which time a newer request may own the ref.
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const seq = ++requestSeqRef.current;
      const isCurrent = () => seq === requestSeqRef.current;

      setIsLoading(true);
      setError(null);

      const endpoint = buildListEndpoint({
        filters: query,
        search,
        page: pageNum,
        pageSize: pageSizeNum,
      });

      try {
        const response = await APIService.makeAPIGetRequest({
          requestString: endpoint,
          accessToken: await getAccessToken(),
          signal: controller.signal,
        });

        if (response.ok) {
          const data = (await response.value) as PatientsResponse & { total_pages?: number };
          // A newer fetch has started; this answer is about a view the user left.
          if (!isCurrent()) return;

          if (!appliedFiltersMatch(data.applied_filters, query, search)) {
            setFiltersUnavailable(true);
            setPatients([]);
            setColumnsByFilter({ definitionId: requirementKey(query), columns: {} });
            setTotalRows(0);
            setTotalPages(0);
            return;
          }
          setFiltersUnavailable(false);
          // A link past the last page: go back to the first rather than show
          // an empty page with no way to page back.
          if (data.patients.length === 0 && data.total > 0 && pageNum > 0) {
            writeView(current => ({ ...current, page: 0 }), 'replace');
            return;
          }
          setPatients(data.patients);

          // Built from the response rather than held across fetches: a stale
          // column beside a fresh row would show one patient's requirement
          // against another's name.
          const columns: Record<string, RequirementColumnView> = {};
          for (const patient of data.patients as ListRow[]) {
            const blocks = patient.patient_requirements
              ? Object.values(patient.patient_requirements)
              : patient.patient_requirement
                ? [patient.patient_requirement]
                : [];
            const column = toRequirementColumnForBlocks(
              blocks,
              selectedValues(query, 'missing').length,
            );
            if (column) columns[patient.patient_id] = column;
          }
          setColumnsByFilter({ definitionId: requirementKey(query), columns });

          setTotalRows(data.total);
          const pages = data.totalPages || data.total_pages || Math.ceil(data.total / pageSizeNum);
          setTotalPages(pages);

          if (search) {
            searchMetrics.performed({
              search_type: 'patient',
              search_context: 'list_page',
              query_length: search.length,
              results_count: data.total,
              has_results: data.total > 0,
            });
          } else if (!isInitialFetch.current) {
            patientMetrics.listViewed({
              total_count: data.total,
              filtered: hasAnyFilter(query),
              page: pageNum + 1,
              page_size: pageSizeNum,
            });
          }

          isInitialFetch.current = false;
        } else {
          // The failure itself, so an AbortError keeps its identity below.
          throw response.error ?? new Error('Failed to fetch patients');
        }
      } catch (err) {
        // A late failure must not blank the list either.
        if (!isCurrent()) return;
        // Reachable on unmount: the cleanup aborts without superseding.
        if ((err as Error).name !== 'AbortError') {
          console.error('Error fetching patients:', err);
          setError('Something went wrong while loading patients. Please try again.');

          SentryService.captureException(err, {
            level: 'error',
            tags: {
              component: 'usePatientList',
              feature: 'patients',
              operation: search ? 'search' : 'list',
            },
            // Filter categories only: the search text and selected ids stay out.
            extra: {
              method: 'fetchPatients',
              endpoint: endpoint.split('?')[0],
              page: pageNum,
              page_size: pageSizeNum,
              filter_categories: Object.keys(query),
              has_search: Boolean(search),
            },
          });

          setPatients([]);
          setTotalRows(0);
          setTotalPages(0);
        }
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    },
    [getAccessToken, patientMetrics, searchMetrics, writeView],
  );

  // Debounced for typing; filter and page changes fetch immediately.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((search: string, pageNum: number, pageSizeNum: number, query: FiltersState) => {
      void fetchPatients(search, pageNum, pageSizeNum, query);
    }, 300),
    [fetchPatients],
  );

  // Back and Forward change the view without an action, so they retire the
  // in-flight request here; a debounced search fetch would otherwise leave a
  // window for the old answer to land under the restored view.
  useEffect(() => {
    requestSeqRef.current += 1;
  }, [location.key]);

  // Search changes are debounced; everything else fetches now. Tracking the
  // last search seen tells the two apart.
  const lastSearchRef = useRef(searchTerm);
  useEffect(() => {
    const searchChanged = lastSearchRef.current !== searchTerm;
    lastSearchRef.current = searchTerm;
    if (searchChanged && searchTerm) {
      debouncedFetch(searchTerm, page, pageSize, filters);
    } else {
      debouncedFetch.cancel();
      void fetchPatients(searchTerm, page, pageSize, filters);
    }

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchTerm, page, pageSize, filters, fetchPatients, debouncedFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────

  const setFilters = useCallback(
    (next: FiltersState, source: FilterSource = 'filter_bar') => {
      invalidateInFlight();
      const previous = pendingViewRef.current.filters;
      const previousStatus = activityStatus(previous);
      const nextStatus = activityStatus(next);
      if (previousStatus !== nextStatus) {
        patientMetrics.activityFilterSelected(nextStatus === null ? null : nextStatus === 'active');
      }
      for (const category of CATEGORIES) {
        const before = selectedValues(previous, category);
        const after = selectedValues(next, category);
        if (before.length !== after.length || before.some(value => !after.includes(value))) {
          patientMetrics.listFilterChanged({ category, value_count: after.length, source });
        }
      }
      writeView(current => ({ ...current, filters: next, page: 0 }));
    },
    [writeView, invalidateInFlight, patientMetrics],
  );

  const toggleFilterValue = useCallback(
    (category: ListCategory, value: string, source: FilterSource = 'sidebar') => {
      setFilters(toggleValue(pendingViewRef.current.filters, category, value), source);
    },
    [setFilters],
  );

  const setActivityFilter = useCallback(
    (active: boolean | null) => {
      const { [LIST_FIELDS.status]: _status, ...rest } = pendingViewRef.current.filters;
      setFilters(
        active === null
          ? rest
          : { ...rest, [LIST_FIELDS.status]: { kind: 'boolean', value: active } },
      );
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    invalidateInFlight();
    patientMetrics.listFiltersClearClicked('filters');
    writeView(current => ({ ...current, filters: {}, page: 0 }));
  }, [writeView, invalidateInFlight, patientMetrics]);

  const setSearchTerm = useCallback(
    (term: string) => {
      invalidateInFlight();
      setSearchTermState(term);
      const now = Date.now();
      const typing = now - lastSearchWriteRef.current < 1000;
      writeView(current => ({ ...current, search: term, page: 0 }), typing ? 'replace' : 'push');
      // After the write, which resets the burst on a push.
      lastSearchWriteRef.current = now;
    },
    [writeView, invalidateInFlight, setSearchTermState],
  );

  const clearSearch = useCallback(() => {
    invalidateInFlight();
    if (searchTerm) {
      searchMetrics.cleared({
        search_type: 'patient',
        search_context: 'list_page',
        had_results: patients.length > 0,
        query_length: searchTerm.length,
      });
    }
    lastSearchWriteRef.current = 0;
    setSearchTermState('');
    writeView(current => ({ ...current, search: '', page: 0 }));
  }, [
    searchTerm,
    patients.length,
    searchMetrics,
    writeView,
    invalidateInFlight,
    setSearchTermState,
  ]);

  const resetView = useCallback(() => {
    invalidateInFlight();
    patientMetrics.listFiltersClearClicked('all');
    lastSearchWriteRef.current = 0;
    setSearchTermState('');
    writeView(() => ({ filters: {}, search: '', page: 0 }));
  }, [writeView, invalidateInFlight, patientMetrics, setSearchTermState]);

  const refresh = useCallback(() => {
    void fetchPatients(searchTerm, page, pageSize, filters);
  }, [fetchPatients, searchTerm, page, pageSize, filters]);

  const navigateToPatient = useCallback(
    (patientId: string) => {
      patientMetrics.selected(patientId, {
        source: 'list',
        context: 'viewing',
      });
      navigate(`/patient/${patientId}`);
    },
    [navigate, patientMetrics],
  );

  const handlePatientCreated = useCallback(
    (_patientId: string) => {
      refresh();
    },
    [refresh],
  );

  const status = activityStatus(filters);

  return {
    patients,
    isLoading,
    error,
    filtersUnavailable,
    searchTerm,
    page,
    pageSize,
    totalRows,
    totalPages,
    hasNextPage: page < totalPages - 1,
    hasPreviousPage: page > 0,
    isEmpty: !isLoading && patients.length === 0,
    isSearchActive: searchTerm.length > 0,
    filters,
    hasFilters: hasAnyFilter(filters),
    activityFilter: status === null ? null : status === 'active',
    setActivityFilter,
    setFilters,
    toggleFilterValue,
    clearFilters,
    resetView,
    setSearchTerm,
    setPage,
    setPageSize,
    clearSearch,
    requirementColumns: columnsForActiveFilter(requirementKey(filters), columnsByFilter),
    refresh,
    navigateToPatient,
    handlePatientCreated,
  };
}
